import { observer } from 'mobx-react';
import { useStore } from 'app/context';
import { useMemo } from 'react';
import colors from 'assets/scss/colors.module.scss';
import { TimeSeriesChart, ITimeSeriesChartConfig } from 'app/components/common/charts/TimeSeriesChart';

interface IGupHistoryChart {
	agtId?: number;
	agtIds?: number[];
}

interface GupChartDataMulti {
	bm: string;
	bmRaw: number;
	countAgtsWithMin3NewReviews: number;
	totalAgencies: number;
	avgBewertungsdurchschnitt: number;
}

interface GupChartDataSingle {
	bm: string;
	bmRaw: number;
	neueBewertungen: number;
}

export const GupHistoryChart = observer((props: IGupHistoryChart) => {
	const { agtGoogleProfileUiStore } = useStore();
	const { agtId, agtIds } = props;

	// Determine if this is a single agency view
	// Single agency if: explicit agtId is provided OR agtIds array has only one element
	const isSingleAgency = agtId !== undefined || (agtIds !== undefined && agtIds.length === 1);

	// Get the actual agency ID for single agency view
	const singleAgtId = agtId || (agtIds && agtIds.length === 1 ? agtIds[0] : undefined);

	// Get current year for title
	const currentYear = new Date().getFullYear();

	// Build chart configuration for MULTI-AGENCY view
	const multiAgencyConfig: ITimeSeriesChartConfig<GupChartDataMulti> = useMemo(
		() => ({
			title: `Google Unternehmensprofil - Agenturen mit ≥ 3 neuen Bewertungen (Jahr ${currentYear})`,
			tag: 'GUP',
			tagColor: 'green',
			height: 400,

			xAxisField: 'bm',
			xAxisRotation: 45,

			// Define Y-axes
			yAxes: [
				{
					name: 'Anzahl Agenturen',
					position: 'left',
				},
				{
					name: 'Bewertungsdurchschnitt',
					position: 'right',
					max: 5,
					formatter: (v) => v.toFixed(1),
					splitLine: false,
				},
			],

			// Define derived fields
			derivedFields: [
				{
					fieldName: 'agtsWithoutMin3NewReviews',
					calculate: (d) => d.totalAgencies - d.countAgtsWithMin3NewReviews,
				},
			],

			// Define series
			series: [
				{
					name: 'Agenturen mit ≥ 3 neuen Bewertungen',
					dataField: 'countAgtsWithMin3NewReviews',
					yAxisIndex: 0,
					chartType: 'bar',
					stack: 'total',
					color: colors.green,
				},
				{
					name: '', // Empty name to hide from legend
					dataField: 'agtsWithoutMin3NewReviews',
					yAxisIndex: 0,
					chartType: 'bar',
					stack: 'total',
					color: colors.softLime,
				},
				{
					name: 'Bewertungsdurchschnitt',
					dataField: 'avgBewertungsdurchschnitt',
					yAxisIndex: 1,
					chartType: 'line',
					smooth: true,
					color: colors.red,
					symbolSize: 8,
					lineStyle: { width: 2 },
				},
			],

			// Custom tooltip to show "X von Y Agenturen"
			tooltipFormatter: (params: any) => {
				if (!Array.isArray(params) || params.length === 0) return '';

				const xValue = params[0]?.axisValue || '';

				let result = `<strong>${xValue}</strong><br/>`;

				// Find countAgtsWithMin3NewReviews and totalAgencies from params
				let countWith3Reviews = 0;
				let totalAgts = 0;
				let avgScore = 0;

				params.forEach((param: any) => {
					if (param.seriesName === 'Agenturen mit ≥ 3 neuen Bewertungen') {
						countWith3Reviews = param.value;
					} else if (param.seriesName === '') {
						// This is the light green bar
						totalAgts = countWith3Reviews + param.value;
					} else if (param.seriesName === 'Bewertungsdurchschnitt') {
						avgScore = param.value;
					}
				});

				result += `${params[0].marker} <strong>${countWith3Reviews} von ${totalAgts} Agenturen</strong> mit ≥ 3 neuen Bewertungen<br/>`;
				result += `${params[2]?.marker || ''} Bewertungsdurchschnitt: <strong>${avgScore.toFixed(1)}</strong><br/>`;

				return result;
			},

			// Data loader function
			dataLoader: async (ids) => {
				if (Array.isArray(ids)) {
					return agtGoogleProfileUiStore.loadGupHistoryCurrentYearMultiAgency(ids);
				} else {
					return agtGoogleProfileUiStore.loadGupHistoryCurrentYearMultiAgency([ids]);
				}
			},
		}),
		[agtGoogleProfileUiStore, currentYear],
	);

	// Build chart configuration for SINGLE-AGENCY view
	const singleAgencyConfig: ITimeSeriesChartConfig<GupChartDataSingle> = useMemo(
		() => ({
			title: `Neue Bewertungen (Jahr ${currentYear})`,
			tag: 'GUP',
			tagColor: 'green',
			height: 400,

			xAxisField: 'bm',
			xAxisRotation: 45,

			// Define Y-axes - single axis for count
			yAxes: [
				{
					name: 'Anzahl neue Bewertungen',
					position: 'left',
				},
			],

			// Define derived fields
			derivedFields: [
				{
					fieldName: 'benchmark',
					calculate: () => 3, // Benchmark constant at 3
				},
			],

			// Define series
			series: [
				{
					name: 'Neue Bewertungen',
					dataField: 'neueBewertungen',
					yAxisIndex: 0,
					chartType: 'bar',
					color: colors.green,
				},
				{
					name: 'Benchmark (3)',
					dataField: 'benchmark',
					yAxisIndex: 0,
					chartType: 'line',
					color: colors.red,
					lineStyle: { type: 'dashed', width: 2 },
					symbolSize: 0, // No symbols on benchmark line
				},
			],

			// Custom tooltip
			tooltipFormatter: (params: any) => {
				if (!Array.isArray(params) || params.length === 0) return '';

				const xValue = params[0]?.axisValue || '';
				let neueBew = 0;

				params.forEach((param: any) => {
					if (param.seriesName === 'Neue Bewertungen') {
						neueBew = param.value;
					}
				});

				let result = `<strong>${xValue}</strong><br/>`;
				result += `${params[0]?.marker} Neue Bewertungen: <strong>${neueBew}</strong><br/>`;
				result += `${params[1]?.marker || ''} Benchmark: <strong>3</strong><br/>`;

				return result;
			},

			// Data loader function
			dataLoader: async (ids) => {
				// Use singleAgtId if available, otherwise extract from ids parameter
				const id = singleAgtId || (Array.isArray(ids) ? ids[0] : ids);
				return agtGoogleProfileUiStore.loadGupHistoryCurrentYearSingleAgency(id);
			},
		}),
		[agtGoogleProfileUiStore, currentYear, singleAgtId],
	);

	// Render the appropriate chart based on view type
	if (isSingleAgency) {
		return <TimeSeriesChart config={singleAgencyConfig} agtId={agtId} agtIds={agtIds} />;
	} else {
		return <TimeSeriesChart config={multiAgencyConfig} agtId={agtId} agtIds={agtIds} />;
	}
});
